import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Page-level table state: search, sort, pagination, view mode, filters.
 * Stored as string key-value pairs per table.
 */
export type PageState = Record<string, string>;

interface TablePageStateStore {
  /** Per-table page-level state */
  pageStates: Record<string, PageState>;
  /** Get page state for a specific table */
  getPageState: (storageKey: string) => PageState;
  /** Set page state for a specific table */
  setPageState: (storageKey: string, values: PageState) => void;
  /** Clear page state for a specific table */
  clearPageState: (storageKey: string) => void;
  /** Clear all page states */
  clearAllPageStates: () => void;
}

/**
 * Zustand store for page-level table UI state persistence.
 *
 * Stores search, sort, pagination, view mode, and filter state for all tables.
 * Used by the useTablePageState hook. Single localStorage key for all tables.
 *
 * localStorage key: 'helios-table-UI-state-storage'
 */
export const useTablePageStateStore = create<TablePageStateStore>()(
  persist(
    (set, get) => ({
      pageStates: {},

      getPageState: (storageKey: string) => get().pageStates[storageKey] || {},

      setPageState: (storageKey: string, values: PageState) => {
        set((prev) => ({
          pageStates: {
            ...prev.pageStates,
            [storageKey]: values,
          },
        }));
      },

      clearPageState: (storageKey: string) => {
        set((prev) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [storageKey]: _, ...rest } = prev.pageStates;
          return { pageStates: rest };
        });
      },

      clearAllPageStates: () => {
        set({ pageStates: {} });
      },
    }),
    {
      name: 'helios-table-UI-state-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ pageStates: state.pageStates }),
    }
  )
);
